import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { setUpdateDrmTableData, setResetUpdateDrmTableDataState } from "../redux/reducers/drmUpdateTableDataSlice";
import { setCancelDrmDailyRecord, resetCancelDrmDailyRecordState } from "../redux/reducers/drmDailyRecordCancelSlice";

import { getDailyUpdatedPayload, parseLocalDate } from "../helpers";

const ALLOWED_CANCEL_STATUSES = new Set([0, 3, 8, 12, 15, 18]);
const RESTRICT_SUBMIT_STATUSES = new Set([7, 9, 10, 11, 13, 14, 16, 17, 19]);
const RESTRICTED_FIELDS = new Set([-1]);
const RESTRICTED_CHANGE_FIELDS = new Set(["changeComment", "modeOfTransport"]);
const NEW_OR_DUPLICATED_STATUSES = new Set([-3, -2]);

const REQUIRED_ACCESSORS = [
  "carrierScac",
  "serviceScac",
  "tariffId",
  "tariffNumber",
  "orgHub",
  "originZone",
  "originCountry",
  "equipmentType",
  "destHub",
  "destinationZone",
  "destinationCountry",
  "effectiveDate",
  "expirationDate",
  "modeOfTransport",
  "rate",
];

const NUMBER_ACCESSORS = [
  "lineHaulRate",
  "rate",
  "pctAward",
  "maxCarrierAllocationPercentage",
  "maxShipmentsAllocated",
];

const FLEXIBLE_ACCESSOR_MAP = {
  orgHub: ["destinationZone", "destHub"],
  originZone: ["destinationZone", "destHub"],
  destinationZone: ["orgHub", "originZone"],
  destHub: ["orgHub", "originZone"],
};

const isEmptyValue = (value) => value === undefined || value === null || value === "" || value === "-";

const hasMissingRequiredFields = (records = []) =>
  records.some((record = {}) =>
    REQUIRED_ACCESSORS.some((accessor) => {
      const value = record?.[accessor];

      if (!isEmptyValue(value)) {
        return false;
      }

      const flexibleFallbacks = FLEXIBLE_ACCESSOR_MAP[accessor];

      if (!flexibleFallbacks || flexibleFallbacks.length === 0) {
        return true;
      }

      const hasFallbackValue = flexibleFallbacks.some((fallbackAccessor) => {
        const fallbackValue = record?.[fallbackAccessor];
        return !isEmptyValue(fallbackValue);
      });

      return !hasFallbackValue;
    })
  );

const getMissingColumn = (records = []) => {
  for (const record of records) {
    for (const accessor of REQUIRED_ACCESSORS) {
      const value = record?.[accessor];

      if (!isEmptyValue(value)) continue;

      const flexibleFallbacks = FLEXIBLE_ACCESSOR_MAP[accessor];

      if (!flexibleFallbacks || flexibleFallbacks.length === 0) {
        return accessor;
      }

      const hasFallbackValue = flexibleFallbacks.some(
        (fallbackAccessor) => !isEmptyValue(record?.[fallbackAccessor])
      );

      if (!hasFallbackValue) return accessor;
    }
  }
  return "";
};

const validateNumberFields = (records = []) =>
  records.some((record = {}) =>
    NUMBER_ACCESSORS.some((accessor) => {
      const value = record?.[accessor];

      if (value === undefined || value === null || value === "" || value === "-") {
        return false;
      }
      const numericValue = Number(value);
      return Number.isNaN(numericValue) || numericValue <= 0;
    })
  );

const handleRestrictionOfSomeFieldChanges = (records = [], originalData = []) =>
  records.some((record = {}) => {
    const status = record?.status;
    if (!RESTRICTED_FIELDS.has(status)) return false;

    const originalRecord = originalData.find((orig) => orig?.sagaId === record?.sagaId) || {};
    const changedKeys = Object.keys(record).filter(
      (key) => record[key] !== originalRecord[key]
    );

    return changedKeys.length > 0 && changedKeys.every((key) => RESTRICTED_CHANGE_FIELDS.has(key));
  });


const hasInvalidDatesForNewOrDuplicatedRecords = (records = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return records.some((record = {}) => {
    if (!NEW_OR_DUPLICATED_STATUSES.has(record?.status)) return false;

    const effectiveDate = parseLocalDate(record?.effectiveDate);
    const expirationDate = parseLocalDate(record?.expirationDate);

    if (effectiveDate && effectiveDate < today) return true;
    if (expirationDate && expirationDate < today) return true;
    if (effectiveDate && expirationDate && effectiveDate > expirationDate) return true;

    return false;
  });
};

const hasRestrictedSubmitStatus = (rows = []) =>
  rows.some((row) => RESTRICT_SUBMIT_STATUSES.has(row?.original?.status));

export const useDailyTableOperations = (dispatch, drmTableData) => {
  const [apiSnackbar, setApiSnackbar] = useState({
    message: "",
    severity: "",
    open: false,
  });

  const {
    errorMessage: updateErrorMsg = "",
    successMessage: updateSuccessMsg = "",
    isLoading: updateIsLoading = false,
  } = useSelector((state) => state?.drmUpdates || {});
  const {
    errorMessage: cancelErrorMsg = "",
    successMessage: cancelSuccessMsg = "",
    isLoading: cancelIsLoading = false,
  } = useSelector((state) => state?.drmDailyRecordCancel || {});
  const [validation, setValidation] = useState({
    isValidate: false,
    message: "",
  });
  const { userInfo } = useSelector((state) => state.user);
  const { t } = useTranslation();

  const triggerRequiredFieldsWarning = useCallback((missingColumn) => {
    setValidation({
      isValidate: true,
      message: `${t("daily_required_fields_missing")} ${t(missingColumn)}`,
    });
  }, []);

  const triggerRestrictedSubmitWarning = useCallback(() => {
    setValidation({
      isValidate: true,
      message: t("daily_submit_restricted_by_status"),
    });
  }, []);

  const triggerRestrictionOfSomeFieldChangesWarning = useCallback(() => {
    setValidation({
      isValidate: true,
      message: t("daily_submit_restriction_of_some_field_changes"),
    });
  }, []);

  const triggerInvalidDatesForNewOrDuplicatedWarning = useCallback(() => {
    setValidation({
      isValidate: true,
      message: t("daily_new_or_duplicated_record_date_validation"),
    });
  }, []);

  const triggerValidationWarning = useCallback(() => {
    setApiSnackbar({
      message: t("must_be_greater_than_zero"),
      severity: "warning",
      open: true,
    });
  }, []);

  const categorizeRecords = useCallback(() => {
    const selectedRows = drmTableData?.selectedRows || [];
    const modifiedRowIds = Object.keys(drmTableData?.modifiedRows || {});
    const newAddedRows = drmTableData?.newAddedRows || [];
    const tableDetails = drmTableData?.drmMaintenanceDetails || [];

    const existingRecords = [];
    const newRecords = [];
    const duplicatedRecords = [];
    const modifiedExistingRecords = [];

    selectedRows.forEach((row) => {
      const rowData = row?.original || {};
      const rowIndex = row?.index;

      const uniqueId = rowData?.sagaId || `new_record_${rowIndex}`;
      const isModified = modifiedRowIds.includes(uniqueId);

      const isNewRecord =
        !rowData?.sagaId ||
        rowData?.sagaId === "" ||
        Object.values(rowData || {}).some(
          (value) =>
            typeof value === "string" && value.includes("Add New Record")
        );

      const isDuplicated =
        newAddedRows.includes(rowIndex) && rowData?.sagaId && !isNewRecord;
      const currentData = tableDetails[rowIndex] || rowData;

      if (isNewRecord) {
        newRecords.push({
          index: rowIndex,
          uniqueId,
          data: currentData,
          isModified,
        });
      } else if (isDuplicated) {
        const cleanedData = { ...currentData };
        delete cleanedData.sagaId;
        delete cleanedData.versionNumber;
        delete cleanedData.id;

        duplicatedRecords.push({
          index: rowIndex,
          uniqueId,
          data: cleanedData,
          originalSagaId: rowData?.sagaId,
          isModified,
        });
      } else if (isModified) {
        modifiedExistingRecords.push({
          index: rowIndex,
          uniqueId,
          data: currentData,
          sagaId: rowData?.sagaId,
          version: rowData?.versionNumber,
        });
      } else {
        existingRecords.push({
          index: rowIndex,
          uniqueId,
          data: currentData,
          sagaId: rowData?.sagaId,
          version: rowData?.versionNumber,
        });
      }
    });

    return {
      existingRecords,
      newRecords,
      duplicatedRecords,
      modifiedExistingRecords,
    };
  }, [drmTableData]);

  const handleSubmitConfirm = useCallback(() => {
    const controller = new AbortController();
    const selectedRows = drmTableData?.selectedRows || [];

    if (hasRestrictedSubmitStatus(selectedRows)) {
      triggerRestrictedSubmitWarning();
      return () => {
        controller.abort();
      };
    }

    const {
      existingRecords,
      newRecords,
      duplicatedRecords,
      modifiedExistingRecords,
    } = categorizeRecords();

    const recordsToCreate = [...newRecords, ...duplicatedRecords];
    const recordsToUpdate = [...modifiedExistingRecords, ...existingRecords];

    const combinedRecords = [...recordsToCreate, ...recordsToUpdate];
    const combinedData = combinedRecords.map(({ data }) => data);

    if (hasMissingRequiredFields(combinedData)) {
      const missingColumn = getMissingColumn(combinedData);
      triggerRequiredFieldsWarning(missingColumn);
      return () => {
        controller.abort();
      };
    }

    const originalDataObj = drmTableData?.originalData || {};
    const originalDataArray = Object.values(originalDataObj);

    if (handleRestrictionOfSomeFieldChanges(combinedData, originalDataArray)) {
      triggerRestrictionOfSomeFieldChangesWarning();
      return () => {
        controller.abort();
      };
    }

    if (validateNumberFields(combinedData)) {
      triggerValidationWarning();
      return () => {
        controller.abort();
      };
    }

    if (hasInvalidDatesForNewOrDuplicatedRecords(combinedData)) {
      triggerInvalidDatesForNewOrDuplicatedWarning();
      return () => {
        controller.abort();
      };
    }

    if (recordsToCreate.length > 0 || recordsToUpdate.length > 0) {
      const controller = new AbortController();
      const createTableData = recordsToCreate.map((record) => record.data);
      const updatedTableData = recordsToUpdate.map((record) => record.data);

      const { isError, errorMessage, submitData } = getDailyUpdatedPayload(
        originalDataArray,
        drmTableData?.drmUpdatedMaintenanceDetails,
        updatedTableData
      );
      setValidation({ isValidate: isError, message: errorMessage });
      const data = [...createTableData, ...submitData];
      const updatedData = data.map((item) => ({
        ...item,
        userCode: userInfo?.userName,
      }));
      !isError &&
        dispatch(
          setUpdateDrmTableData({
            userCode: userInfo?.userName,
            updatedTableData: updatedData,
            tableName: "drmDailyTableData",
            controller,
          })
        );
    }
  }, [categorizeRecords, drmTableData, userInfo, triggerRequiredFieldsWarning, triggerRestrictedSubmitWarning, triggerRestrictionOfSomeFieldChangesWarning, triggerInvalidDatesForNewOrDuplicatedWarning]);

  const handleCancelConfirm = useCallback(() => {
    const selectedRows = drmTableData?.selectedRows || [];

    const isAllowingCancel = selectedRows.every((row) =>
      ALLOWED_CANCEL_STATUSES.has(row?.original?.status)
    );

    if (isAllowingCancel) {
      dispatch(
        setCancelDrmDailyRecord({
          userCode: userInfo?.userName,
          details: selectedRows.map((row) => ({
            sagaId: row?.original?.sagaId,
            versionNumber: row?.original?.versionNumber,
          })),
        })
      );
    } else {
      setApiSnackbar({
        message: t('cancel_not_allowed_message'),
        severity: "warning",
        open: true,
      });
    }
  }, [drmTableData, userInfo]);

  useEffect(() => {
    if (validation.isValidate) {
      setApiSnackbar({
        message: validation.message,
        severity: "warning",
        open: true,
      });
      setValidation({ isValidate: false, message: "" });
    }
  }, [validation]);

  useEffect(() => {
    if (!updateIsLoading && updateSuccessMsg) {
      setApiSnackbar({
        message: updateSuccessMsg,
        severity: "info",
        open: true,
      });
      dispatch(setResetUpdateDrmTableDataState());
    } else if (!updateIsLoading && updateErrorMsg) {
      setApiSnackbar({
        message: updateErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(setResetUpdateDrmTableDataState());
    }
  }, [updateIsLoading, updateSuccessMsg, updateErrorMsg]);

  useEffect(() => {
    if (!cancelIsLoading && cancelSuccessMsg) {
      setApiSnackbar({
        message: cancelSuccessMsg,
        severity: "info",
        open: true,
      });
      dispatch(resetCancelDrmDailyRecordState());
    } else if (!cancelIsLoading && cancelErrorMsg) {
      setApiSnackbar({
        message: cancelErrorMsg,
        severity: "error",
        open: true,
      });
      dispatch(resetCancelDrmDailyRecordState());
    }
  }, [cancelIsLoading, cancelErrorMsg, cancelSuccessMsg]);

  const apiSnackClose = () => {
    setApiSnackbar({ message: "", severity: "", open: false });
  };

  return {
    apiSnackbar,
    apiSnackClose,
    handleSubmitConfirm,
    categorizeRecords,
    handleCancelConfirm,
  };
};
